// File: @/hooks/use-engagement-tracking.ts

'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { contentCompletionEvent, qualifiedVisitEvent, readTimeEvent, track } from '@/lib/analytics';

// ===================================
// TYPES
// ===================================

export interface UseEngagementTrackingOptions {
  /** Content identifier for tracking */
  readonly contentId: string;

  /** Content category */
  readonly category: string;

  /** Content type for completion tracking */
  readonly contentType?: string;

  /** Whether tracking is enabled (defaults to true) */
  readonly enabled?: boolean;
}

// ===================================
// CONSTANTS
// ===================================

/** Qualified-visit threshold: 30 seconds AND 25% scroll */
const ENGAGEMENT_TIME_THRESHOLD_MS = 30_000;
const ENGAGEMENT_SCROLL_THRESHOLD_PCT = 25;

/** Content-completion threshold: 60 seconds AND 75% scroll */
const COMPLETION_TIME_THRESHOLD_S = 60;
const COMPLETION_SCROLL_THRESHOLD_PCT = 75;

// ===================================
// HOOKS
// ===================================

/**
 * Sends total reading time once when the page is hidden. `pagehide` fires on
 * mobile Safari and bfcache navigations where `beforeunload` doesn't; the ref
 * prevents a resend after a bfcache restore.
 */
function useReadTimeOnPageHide(startTimeRef, contentId, category, enabled) {
  const $ = _c(6);
  const hasTrackedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== category || $[1] !== contentId || $[2] !== enabled || $[3] !== startTimeRef) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      const handlePageHide = () => {
        if (hasTrackedRef.current) {
          return;
        }
        hasTrackedRef.current = true;
        const readTimeS = Math.round((Date.now() - startTimeRef.current) / 1000);
        track(readTimeEvent(readTimeS, contentId, category));
      };
      window.addEventListener("pagehide", handlePageHide);
      return () => {
        window.removeEventListener("pagehide", handlePageHide);
      };
    };
    t1 = [startTimeRef, contentId, category, enabled];
    $[0] = category;
    $[1] = contentId;
    $[2] = enabled;
    $[3] = startTimeRef;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
}

/**
 * Hook for tracking user engagement and reading time.
 *
 * Why this exists:
 * - Measures quality engagement (time + scroll combined signal)
 * - Tracks content completion (stronger combined signal)
 * - Captures reading time on page unload
 *
 * Why scroll depth is tracked internally (not passed as a prop):
 * - The previous design passed scrollDepth as a prop from a useRef, which
 *   always read the initial ref value (0) because ref mutations don't
 *   trigger re-renders or effect dependencies. Threshold checks never fired.
 * - Self-contained scroll tracking avoids the stale-closure issue entirely.
 *
 * Both thresholds check on each scroll event, which is sufficient because
 * "engaged" by definition requires active scrolling behaviour.
 *
 * @param options - Engagement tracking configuration
 *
 * @example
 * useEngagementTracking({
 *   contentId: 'homepage',
 *   category: 'landing',
 *   contentType: 'page',
 * });
 */
export function useEngagementTracking(options) {
  const $ = _c(7);
  const {
    contentId,
    category,
    contentType: t0,
    enabled: t1
  } = options;
  const contentType = t0 === undefined ? "page" : t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Date.now();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const startTimeRef = useRef(t2);
  const scrollDepthRef = useRef(0);
  const hasTrackedEngagementRef = useRef(false);
  const hasTrackedCompletionRef = useRef(false);
  let t3;
  let t4;
  if ($[1] !== category || $[2] !== contentId || $[3] !== contentType || $[4] !== enabled) {
    t3 = () => {
      if (!enabled) {
        return;
      }
      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) {
          return;
        }
        const scrollTop = window.pageYOffset;
        scrollDepthRef.current = Math.round(scrollTop / scrollHeight * 100);
        const timeSpentMs = Date.now() - startTimeRef.current;
        if (!hasTrackedEngagementRef.current && timeSpentMs > ENGAGEMENT_TIME_THRESHOLD_MS && scrollDepthRef.current > ENGAGEMENT_SCROLL_THRESHOLD_PCT) {
          hasTrackedEngagementRef.current = true;
          track(qualifiedVisitEvent(timeSpentMs, contentId, category));
        }
        const readingTimeS = Math.round(timeSpentMs / 1000);
        if (!hasTrackedCompletionRef.current && readingTimeS > COMPLETION_TIME_THRESHOLD_S && scrollDepthRef.current > COMPLETION_SCROLL_THRESHOLD_PCT) {
          hasTrackedCompletionRef.current = true;
          track(contentCompletionEvent(contentType, contentId, category));
        }
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [contentId, category, contentType, enabled];
    $[1] = category;
    $[2] = contentId;
    $[3] = contentType;
    $[4] = enabled;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  useReadTimeOnPageHide(startTimeRef, contentId, category, enabled);
}